import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { buildSearchParamsAuto } from '../utils/queryParams';

const HEX_TAG_TYPE = 'Hex';

interface HexReport {
  id: number;
  label: string;
  description?: string;
  [key: string]: any;
}

interface HexReportsResponse {
  results: HexReport[];
  count?: number;
}

interface HexEmbeddedUrlResponse {
  embedded_url: string;
}

interface GetHexReportsParams {
  profile_type: string;
  entity_id?: string | number;
}

interface GetHexReportByIdParams {
  dashboard: string;
  providerId?: string;
}

interface GetHexUrlParams {
  reportId: string;
  providerId: string;
}

export const hexApi = createApi({
  reducerPath: 'hexApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [HEX_TAG_TYPE],
  endpoints: (builder) => ({
    // GET all hex reports for a profile type
    getHexReports: builder.query<HexReportsResponse, GetHexReportsParams>({
      query: ({ profile_type, entity_id }) => {
        const queryString = buildSearchParamsAuto({
          profile_type,
          entity_id,
        }).toString();
        return {
          url: `v1/analytics/hex/dashboards/${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { profile_type, entity_id }) => [
        {
          type: HEX_TAG_TYPE,
          id: `REPORTS_${profile_type}_${entity_id ?? 'all'}`,
        },
      ],
    }),

    // GET hex report embedded URL by dashboard ID
    getHexReportById: builder.query<
      HexEmbeddedUrlResponse,
      GetHexReportByIdParams
    >({
      query: ({ dashboard, providerId }) => {
        const params = buildSearchParamsAuto({ provider_id: providerId });
        const queryString = params.toString();
        return {
          url: `v1/analytics/hex/dashboards/${dashboard}/embedded-link/${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { dashboard, providerId }) => [
        { type: HEX_TAG_TYPE, id: `REPORT_${dashboard}_${providerId || 'global'}` },
      ],
    }),

    // GET hex URL for admin reports (leakage/hospital readmission)
    getHexUrl: builder.query<HexEmbeddedUrlResponse, GetHexUrlParams>({
      query: ({ reportId, providerId }) => {
        const params = buildSearchParamsAuto({ provider_id: providerId });
        const queryString = params.toString();
        return {
          url: `v1/admin/hex/embedded-link/${reportId}/${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { reportId, providerId }) => [
        { type: HEX_TAG_TYPE, id: `URL_${reportId}_${providerId}` },
      ],
    }),
  }),
});

export const {
  useGetHexReportsQuery,
  useGetHexReportByIdQuery,
  useGetHexUrlQuery,
} = hexApi;
